import type { EbookQuestion, EbookMove } from '@wx-weiqi/contracts';
import { boardCoordinate } from '../domain/board-geometry';
export function sourceShowsFullBoard(q: EbookQuestion) {
  return (
    q.visible.x === 0 &&
    q.visible.y === 0 &&
    q.visible.columns === q.size &&
    q.visible.rows === q.size
  );
}
export function outsideModelProblem(q: EbookQuestion) {
  if (q.outside === 'COMPLETE' && !sourceShowsFullBoard(q))
    return '原图是局部棋图；中间的全盘展示不代表图外已知。请选择“我确认按图外空盘练习”后再确认棋形。';
  if (q.outside === 'UNKNOWN') return '请先确认下方“未显示区域”的处理方式，再确认棋形。';
  if (q.outside === 'CONFIRMED_EMPTY' && !q.assumption.trim()) return '请填写图外空盘的建模说明。';
  return '';
}
export function chooseOutsideModel(
  q: EbookQuestion,
  outside: EbookQuestion['outside'],
): EbookQuestion {
  if (outside === 'COMPLETE' && !sourceShowsFullBoard(q))
    throw new Error('局部棋图不能标为原图完整棋盘');
  return {
    ...q,
    outside,
    faceConfirmed: false,
    geometryConfirmed: false,
    answerConfirmed: false,
    // Written only following an explicit teacher choice, never on recognition/load.
    assumption:
      outside === 'CONFIRMED_EMPTY' && !q.assumption.trim()
        ? '教师确认：按当前确认的真实路数与局部位置建模；资料未显示区域视为空盘，仅用于本题练习。'
        : q.assumption,
  };
}
export function questionViewport(q: EbookQuestion) {
  const v = q.visible;
  const dx = boardCoordinate(q.size, 1) - boardCoordinate(q.size, 0);
  return {
    x: boardCoordinate(q.size, v.x) - dx * 0.53,
    y: boardCoordinate(q.size, v.y) - dx * 0.53,
    width: (v.columns + 0.06) * dx,
    height: (v.rows + 0.06) * dx,
  };
}
export function pathFor(q: EbookQuestion, id: string | null): EbookMove[] {
  const path: EbookMove[] = [];
  const seen = new Set<string>();
  while (id) {
    if (seen.has(id)) throw new Error('变化树存在循环');
    seen.add(id);
    const n = q.nodes.find((n) => n.id === id);
    if (!n) throw new Error('变化节点不存在');
    path.unshift(n.move);
    id = n.parentId;
  }
  return path;
}
// Navigation keeps the tree intact; only this explicit command removes nodes.
export function removeAnswerBranch(q: EbookQuestion, id: string): EbookQuestion {
  const removed = new Set([id]);
  let previous = 0;
  while (previous !== removed.size) {
    previous = removed.size;
    for (const n of q.nodes) if (n.parentId && removed.has(n.parentId)) removed.add(n.id);
  }
  return { ...q, nodes: q.nodes.filter((n) => !removed.has(n.id)), answerConfirmed: false };
}
export function questionStage(q: EbookQuestion) {
  if (
    !q.faceConfirmed ||
    !q.geometryConfirmed ||
    q.uncertain.length ||
    q.role !== 'PROBLEM' ||
    outsideModelProblem(q)
  )
    return '待校对';
  if (!q.number.trim() || !q.goal.trim()) return '待补信息';
  if (
    q.mode === 'AUTO' &&
    (!q.answerConfirmed || !q.answerSource.trim() || !q.nodes.some((n) => n.outcome === 'CORRECT'))
  )
    return '待编答案';
  return q.mode === 'AUTO' ? '自动批改就绪' : '手动练习就绪';
}
/** Shared completion gate for the library, arrangement and publication flows. */
export function isEbookQuestionReady(q: EbookQuestion): boolean {
  const stage = questionStage(q);
  return stage === '自动批改就绪' || stage === '手动练习就绪';
}
export function transformQuestion(q: EbookQuestion, kind: 'ROTATE' | 'MIRROR'): EbookQuestion {
  const n = q.size;
  const point = (p: { x: number; y: number }) =>
    kind === 'ROTATE' ? { x: n - 1 - p.y, y: p.x } : { x: n - 1 - p.x, y: p.y };
  const stones: EbookQuestion['stones'] = Array.from({ length: n * n }, () => null);
  q.stones.forEach((s, i) => {
    const p = point({ x: i % n, y: Math.floor(i / n) });
    stones[p.y * n + p.x] = s;
  });
  const v = q.visible;
  const corners = [
    point({ x: v.x, y: v.y }),
    point({ x: v.x + v.columns - 1, y: v.y + v.rows - 1 }),
  ];
  return {
    ...q,
    stones,
    marks: q.marks.map((m) => ({ ...m, point: point(m.point) })),
    uncertain: q.uncertain.map(point),
    nodes: q.nodes.map((node) => ({
      ...node,
      move: { ...node.move, point: node.move.point ? point(node.move.point) : null },
    })),
    visible: {
      x: Math.min(...corners.map((p) => p.x)),
      y: Math.min(...corners.map((p) => p.y)),
      columns: kind === 'ROTATE' ? v.rows : v.columns,
      rows: kind === 'ROTATE' ? v.columns : v.rows,
    },
    geometryConfirmed: false,
    faceConfirmed: false,
    answerConfirmed: false,
  };
}
export function anchorQuestion(
  q: EbookQuestion,
  x: number,
  y: number,
  size: EbookQuestion['size'],
): EbookQuestion {
  if (![x, y].every((n) => Number.isInteger(n) && n >= 0)) throw new Error('锚点必须是非负整数');
  const dx = x - q.visible.x,
    dy = y - q.visible.y;
  const p = (point: { x: number; y: number }) => {
    const result = { x: point.x + dx, y: point.y + dy };
    if (result.x < 0 || result.y < 0 || result.x >= size || result.y >= size)
      throw new Error('变换会丢失棋子、标记或答案，请先校对');
    return result;
  };
  const stones: EbookQuestion['stones'] = Array.from({ length: size * size }, () => null);
  q.stones.forEach((s, i) => {
    if (s) {
      const point = p({ x: i % q.size, y: Math.floor(i / q.size) });
      stones[point.y * size + point.x] = s;
    }
  });
  if (x + q.visible.columns > size || y + q.visible.rows > size)
    throw new Error('可见区域超出真实路数');
  return {
    ...q,
    size,
    stones,
    visible: { ...q.visible, x, y },
    marks: q.marks.map((m) => ({ ...m, point: p(m.point) })),
    uncertain: q.uncertain.map(p),
    nodes: q.nodes.map((n) => ({
      ...n,
      move: { ...n.move, point: n.move.point ? p(n.move.point) : null },
    })),
    geometryConfirmed: false,
    faceConfirmed: false,
    answerConfirmed: false,
  };
}
export function resizeQuestion(q: EbookQuestion, size: EbookQuestion['size']) {
  return anchorQuestion(
    q,
    q.visible.x + q.visible.columns === q.size ? size - q.visible.columns : q.visible.x,
    q.visible.y + q.visible.rows === q.size ? size - q.visible.rows : q.visible.y,
    size,
  );
}
